import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "@api/base-services-axios-rtx";
import { AgencyEndpoints } from "@/shared/models/endpoints/agency.endpoint";
import { createQuery } from "@/shared/helpers/create-query.helper";
import { I_GetAgenciesPublicValuesQRTK } from "./values/agencies-public-list.values";
import { IAgenciesPublicResponse } from "./responses/agencies-public-list.response";
import { AgenciesPrivateListResponse } from "./responses/agencies-private-list.response";
import { I_GetAgenciesValuesQRTK } from "./values/agencies-private-list.values";
import { I_CreateAgenciesValuesQRTK } from "./values/agencies-create.values";
import getFormData from "@/src-refactor/modules/shared/helpers/object-to-form-data";
import { IAgenciesCreateResponse } from "./responses/agencies-create.response";
import { I_GetAgencyValuesQRTK } from "./values/agencies-detail.values";
import { IAgenciesDetailResponse } from "./responses/agencies-detail.response";
import { I_UpdateAgenciesValuesQRTK } from "./values/agencies-update.values";
import { IAgencyUpdateResponse } from "./responses/agencies-update.response";
import { I_ActivateAgencyValuesQRTK } from "./values/agencies-activate.values";
import { I_DeactivateAgencyValuesQRTK } from "./values/agencies-deactivate.values";
import { mainSplitApi } from "../../mainSplitApi";
import {
  remaxGlobalDetailMock,
  remaxGlobalListMock
} from "@rf/mock/remax_global.mock";
import AGENCY_TAG_TYPES from "./agency.tags";

/**
 * @deprecated
 */
export const AgencyQueryServices = createApi({
  reducerPath: "agency-services-rf",
  baseQuery: axiosBaseQuery(),

  endpoints: builder => ({
    getAgenciesPublic: builder.mutation({
      query: (values: I_GetAgenciesPublicValuesQRTK) => ({
        url: AgencyEndpoints.agencyPublicList + createQuery(values.query),
        method: "GET"
      }),
      transformResponse: (response: IAgenciesPublicResponse, meta, arg) =>
        response.data
    }),
    getAgencies: builder.mutation({
      query: (values: I_GetAgenciesValuesQRTK) => ({
        url: AgencyEndpoints.agencyPrivateList + createQuery(values.query),
        method: "GET"
      }),
      transformResponse: (response: AgenciesPrivateListResponse, meta, arg) =>
        response
    }),

    createAgency: builder.mutation({
      query: (values: I_CreateAgenciesValuesQRTK) => ({
        url: AgencyEndpoints.agencyCreate,
        method: "POST",
        body: getFormData(values.body)
      }),
      transformResponse: (response: IAgenciesCreateResponse, meta, arg) => response
    }),

    agencyDetail: builder.mutation({
      query: (values: I_GetAgencyValuesQRTK) => ({
        url: AgencyEndpoints.agenciesDetail(values.params?.id!),
        method: "GET"
      }),
      transformResponse: (response: IAgenciesDetailResponse, meta, arg) => {
        return {
          ...response,
          ...remaxGlobalDetailMock(response)
        };
      }
    }),

    updateAgency: builder.mutation({
      query: (values: I_UpdateAgenciesValuesQRTK) => ({
        url: AgencyEndpoints.agenciesUpdate(values.params.id),
        method: "POST",
        body: getFormData(values.body)
      }),
      transformResponse: (response: IAgencyUpdateResponse, meta, arg) => response
    }),

    activateAgency: builder.mutation({
      query: (values: I_ActivateAgencyValuesQRTK) => ({
        url: AgencyEndpoints.agenciesActivate(values.params.id),
        method: "POST",
        body: getFormData(values.body)
      }),
      transformResponse: (response: any, meta, arg) => response
    }),

    deactivateAgency: builder.mutation({
      query: (values: I_DeactivateAgencyValuesQRTK) => ({
        url: AgencyEndpoints.agenciesDeactivate(values.params.id),
        method: "POST",
        body: getFormData(values.body)
      }),
      transformResponse: (response: any, meta, arg) => response
    })
  })
});

export const AgenciesQueryServicesRf = mainSplitApi.injectEndpoints({
  endpoints: ({ mutation, query }) => ({
    getAgenciesPublic: query<IAgenciesPublicResponse, I_GetAgenciesPublicValuesQRTK>(
      {
        query: values => ({
          url: AgencyEndpoints.agencyPublicList,
          params: values.query,
          method: "GET"
        }),
        providesTags: []
      }
    ),
    getAgencies: query<AgenciesPrivateListResponse, I_GetAgenciesValuesQRTK>({
      query: values => ({
        url: AgencyEndpoints.agencyPrivateList,
        params: values.query,
        method: "GET"
      }),
      transformResponse: (response: AgenciesPrivateListResponse, meta, arg) => {
        return {
          ...response,
          data: remaxGlobalListMock(response.data)
        };
      },
      providesTags: [AGENCY_TAG_TYPES.AGENCY_GET_LIST]
    }),
    createAgency: mutation<IAgenciesCreateResponse, I_CreateAgenciesValuesQRTK>({
      query: values => ({
        url: AgencyEndpoints.agencyCreate,
        method: "POST",
        body: getFormData(values.body)
      }),
      invalidatesTags(result, error, arg, meta) {
        if (result) {
          return [AGENCY_TAG_TYPES.AGENCY_GET_LIST, AGENCY_TAG_TYPES.AGENCY_DETAIL];
        }
        return [];
      }
    }),
    getAgencyDetail: query<IAgenciesDetailResponse, I_GetAgencyValuesQRTK>({
      query: values => ({
        url: AgencyEndpoints.agenciesDetail(values.params?.id!),
        method: "GET"
      }),
      providesTags: [AGENCY_TAG_TYPES.AGENCY_DETAIL]
    }),
    updateAgency: mutation<IAgencyUpdateResponse, I_UpdateAgenciesValuesQRTK>({
      query: values => ({
        url: AgencyEndpoints.agenciesUpdate(values.params.id),
        method: "POST",
        body: getFormData(values.body)
      }),
      invalidatesTags(result, error, arg, meta) {
        if (result) {
          return [AGENCY_TAG_TYPES.AGENCY_GET_LIST, AGENCY_TAG_TYPES.AGENCY_DETAIL];
        }
        return [];
      }
    }),
    activateAgency: mutation<any, I_ActivateAgencyValuesQRTK>({
      query: values => ({
        url: AgencyEndpoints.agenciesActivate(values.params.id),
        method: "POST",
        body: getFormData(values.body)
      }),
      invalidatesTags(result, error, arg, meta) {
        if (result) {
          return [AGENCY_TAG_TYPES.AGENCY_GET_LIST, AGENCY_TAG_TYPES.AGENCY_DETAIL];
        }
        return [];
      }
    }),
    deactivateAgency: mutation<any, I_DeactivateAgencyValuesQRTK>({
      query: values => ({
        url: AgencyEndpoints.agenciesDeactivate(values.params.id),
        method: "POST",
        body: getFormData(values.body)
      }),
      invalidatesTags(result, error, arg, meta) {
        if (result) {
          return [AGENCY_TAG_TYPES.AGENCY_GET_LIST, AGENCY_TAG_TYPES.AGENCY_DETAIL];
        }
        return [];
      }
    })
  })
});

export const {
  useGetAgenciesQuery,
  useGetAgencyDetailQuery,
  useLazyGetAgenciesQuery,
  useUpdateAgencyMutation
} = AgenciesQueryServicesRf;

export const {
  useGetAgenciesPublicMutation,
  useGetAgenciesMutation,
  useCreateAgencyMutation,
  useAgencyDetailMutation,
  // useUpdateAgencyMutation,
  useActivateAgencyMutation,
  useDeactivateAgencyMutation
} = AgencyQueryServices;
